/**
 * The investor's externally held mutual funds, imported from a CAS statement.
 *
 * These are holdings bought away from SMEST. They are read-only here: nothing
 * on this panel can be transacted, because SMEST does not hold the units.
 *
 * WHAT AN ADMIN CAN DO. Import a CAS PDF they already have, by file or by URL,
 * and ask CAMS to email a fresh one to the investor. The Gmail and CDSL import
 * paths on the user platform authenticate as the investor — one needs their
 * Google account, the other an OTP to their mobile — so neither is offered
 * here. See `Api/mfAdminExternalCas` for the reasoning.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailIcon from "@mui/icons-material/MailOutline";
import {
  buildExternalMutualFundPortfolio,
  fetchMFExternalCas,
  generateCasByEmail,
  getMFExternalCasError,
  parseMFExternalCasFile,
  parseMFExternalCasUrl,
} from "../../Api/mfAdminExternalCas";
import type {
  ExternalMutualFundPortfolio,
  MFExternalCasResponse,
} from "../../Api/mfAdminExternalCas";
import styles from "../../pages/MutualFunds/user-investments-dashboard.module.css";

const InlineLoader = () => <span className={styles.inlineSpinner} aria-hidden="true" />;

const currency = (value: number | null, decimals = 0) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: decimals,
      }).format(value);

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

export interface ExternalCasPanelProps {
  userUuid: string;
  investorEmail?: string;
  investorPan?: string;
}

const ExternalCasPanel: React.FC<ExternalCasPanelProps> = ({
  userUuid,
  investorEmail,
  investorPan,
}) => {
  const [portfolio, setPortfolio] = useState<ExternalMutualFundPortfolio | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [fileName, setFileName] = useState("");
  const [dragging, setDragging] = useState(false);
  /* Upstream lets the investor choose the statement period, so an admin
     chasing one particular year can do the same. Defaults to three years. */
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 3);
    return date.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const applyResponse = (response: MFExternalCasResponse | null) => {
    setPortfolio(response ? buildExternalMutualFundPortfolio(response) : null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      applyResponse(await fetchMFExternalCas(userUuid));
    } catch (loadError) {
      setPortfolio(null);
      setError(getMFExternalCasError(loadError));
    } finally {
      setLoading(false);
    }
  }, [userUuid]);

  useEffect(() => {
    load();
  }, [load]);

  const importFile = async (file: File | null) => {
    if (!file) return;
    if (file.type && file.type !== "application/pdf") {
      setError("A CAS statement has to be a PDF.");
      return;
    }
    setFileName(file.name);
    setImporting(true);
    setError("");
    setNotice("");
    try {
      applyResponse(await parseMFExternalCasFile(userUuid, file));
      setNotice(`Imported ${file.name}.`);
    } catch (importError) {
      setError(getMFExternalCasError(importError));
    } finally {
      setImporting(false);
    }
  };

  const importUrl = async () => {
    const url = pdfUrl.trim();
    if (!url) return;
    setImporting(true);
    setError("");
    setNotice("");
    try {
      applyResponse(await parseMFExternalCasUrl(userUuid, url));
      setNotice("Imported the statement from that URL.");
      setPdfUrl("");
    } catch (importError) {
      setError(getMFExternalCasError(importError));
    } finally {
      setImporting(false);
    }
  };

  const requestByEmail = async () => {
    if (!investorEmail || !investorPan) return;
    if (!fromDate || !toDate) {
      setError("Enter the statement period.");
      return;
    }
    if (fromDate > toDate) {
      setError("The statement start date must be before its end date.");
      return;
    }
    setRequesting(true);
    setError("");
    setNotice("");
    try {
      setNotice(
        await generateCasByEmail({
          email: investorEmail,
          fromDate,
          toDate,
          pan: investorPan.trim().toUpperCase(),
        })
      );
    } catch (requestError) {
      setError(getMFExternalCasError(requestError));
    } finally {
      setRequesting(false);
    }
  };

  const holdings = portfolio ? portfolio.holdings : [];

  return (
    <>
      <div className={styles.sectionHeader}>
        <div>
          <h2>External holdings</h2>
          <p>
            Mutual funds this investor holds away from SMEST, read from a CAS
            statement. Read-only — SMEST does not hold these units, so nothing
            here can be transacted.
          </p>
        </div>
        <button
          className={styles.miniButton}
          disabled={loading}
          aria-busy={loading || undefined}
          onClick={load}
        >
          {loading ? <InlineLoader /> : <RefreshIcon style={{ fontSize: 15 }} />} Refresh
        </button>
      </div>

      <div className={styles.form} style={{ marginBottom: 18 }}>
        <div className={styles.formRow}>
          <div className={styles.field}>
            <label>Import a CAS PDF</label>
            <label
              className={`${styles.filePicker} ${dragging ? styles.filePickerActive : ""} ${
                fileName && !importing ? styles.filePickerDone : ""
              }`}
              onDragOver={(event) => {
                event.preventDefault();
                if (!importing) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                if (importing) return;
                importFile(event.dataTransfer.files && event.dataTransfer.files[0]);
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                disabled={importing}
                onChange={(event) =>
                  importFile(event.target.files && event.target.files[0])
                }
              />
              <span className={styles.filePickerIcon}>
                {importing ? <InlineLoader /> : <UploadFileIcon fontSize="small" />}
              </span>
              <span className={styles.filePickerBody}>
                <span className={styles.filePickerTitle}>
                  {importing
                    ? "Importing…"
                    : fileName || "Choose a CAS PDF, or drop one here"}
                </span>
                <span className={styles.filePickerMeta}>
                  {importing
                    ? "Parsing the statement"
                    : "A CAMS/KFintech consolidated statement for this investor"}
                </span>
              </span>
              {fileName && !importing ? (
                <button
                  type="button"
                  className={styles.filePickerClear}
                  onClick={(event) => {
                    event.preventDefault();
                    setFileName("");
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  Clear
                </button>
              ) : null}
            </label>
          </div>
          <div className={styles.field}>
            <label>…or from a statement URL</label>
            <input
              className={styles.input}
              value={pdfUrl}
              disabled={importing}
              placeholder="https://…/cas.pdf"
              onChange={(event) => setPdfUrl(event.target.value)}
            />
            <div className={styles.helper}>
              <button
                className={styles.miniButton}
                disabled={importing || !pdfUrl.trim()}
                aria-busy={importing || undefined}
                onClick={importUrl}
              >
                {importing ? <InlineLoader /> : <UploadFileIcon style={{ fontSize: 15 }} />}
                {importing ? "Importing…" : "Import from URL"}
              </button>
            </div>
          </div>
        </div>

        {investorEmail && investorPan ? (
          <div className={styles.info}>
            <b>No statement to hand? Ask CAMS for one.</b>
            <div className={styles.formRow} style={{ marginTop: 10 }}>
              <div className={styles.field}>
                <label>Statement from</label>
                <input
                  className={styles.input}
                  type="date"
                  value={fromDate}
                  max={toDate}
                  disabled={requesting}
                  onChange={(event) => setFromDate(event.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label>Statement to</label>
                <input
                  className={styles.input}
                  type="date"
                  value={toDate}
                  min={fromDate}
                  disabled={requesting}
                  onChange={(event) => setToDate(event.target.value)}
                />
              </div>
            </div>
            <button
              className={styles.kycButton}
              style={{ marginTop: 10 }}
              disabled={requesting}
              aria-busy={requesting || undefined}
              onClick={requestByEmail}
            >
              {requesting ? <InlineLoader /> : <EmailIcon style={{ fontSize: 16 }} />}
              {requesting ? "Requesting…" : "Email a CAS to the investor"}
            </button>
            <div className={styles.helper} style={{ marginTop: 8 }}>
              CAMS sends it to {investorEmail} for PAN{" "}
              {investorPan.toUpperCase()}. The investor receives it — have them
              forward it back, then import it above.
            </div>
          </div>
        ) : (
          <div className={styles.helper}>
            A CAS can only be emailed when the investor has both an email
            address and a PAN on record.
          </div>
        )}

        {notice && <div className={styles.info}>{notice}</div>}
        {error && <div className={styles.error}>{error}</div>}
      </div>

      {loading ? (
        <div className={styles.info}>
          <InlineLoader /> Loading external holdings…
        </div>
      ) : holdings.length ? (
        <>
          <section className={styles.metricGrid} style={{ marginTop: 0 }}>
            <div className={styles.metricCard}>
              <div className={styles.metricTop}>
                <span>External value</span>
              </div>
              <div className={styles.metricValue}>
                {currency(portfolio ? portfolio.totalCurrentValue : 0)}
              </div>
              <div className={styles.metricHint}>{holdings.length} schemes</div>
            </div>
            <div className={styles.metricCard}>
              <div className={styles.metricTop}>
                <span>Held in demat</span>
              </div>
              <div className={styles.metricValue}>
                {portfolio ? portfolio.dematHoldingCount : 0}
              </div>
              <div className={styles.metricHint}>From the demat section</div>
            </div>
            <div className={styles.metricCard}>
              <div className={styles.metricTop}>
                <span>Held in folios</span>
              </div>
              <div className={styles.metricValue}>
                {portfolio ? portfolio.folioHoldingCount : 0}
              </div>
              <div className={styles.metricHint}>From the statement section</div>
            </div>
            <div className={styles.metricCard}>
              <div className={styles.metricTop}>
                <span>Statement period</span>
              </div>
              <div className={styles.metricValue} style={{ fontSize: 16 }}>
                {formatDate(portfolio ? portfolio.periodFrom : null)} –{" "}
                {formatDate(portfolio ? portfolio.periodTo : null)}
              </div>
              <div className={styles.metricHint}>
                {portfolio && portfolio.statementType
                  ? portfolio.statementType
                  : "CAS"}
              </div>
            </div>
          </section>

          <div className={styles.tableWrap}>
            <table className={styles.table} style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Scheme</th>
                  <th>Source</th>
                  <th>Units</th>
                  <th>NAV</th>
                  <th>Invested</th>
                  <th>Current value</th>
                  <th>Gain</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding) => (
                  <tr key={holding.key}>
                    <td>
                      <div className={styles.fundName}>{holding.name}</div>
                      <div className={styles.fundMeta}>
                        {holding.amc ? `${holding.amc} · ` : ""}
                        {holding.isin}
                        {holding.folioNumber ? ` · Folio ${holding.folioNumber}` : ""}
                      </div>
                    </td>
                    <td>{holding.source === "demat" ? "Demat" : "Folio"}</td>
                    <td>
                      {holding.units.toLocaleString("en-IN", {
                        maximumFractionDigits: 3,
                      })}
                    </td>
                    <td>{currency(holding.nav, 2)}</td>
                    <td>{currency(holding.investedValue)}</td>
                    <td>
                      <b>{currency(holding.currentValue)}</b>
                    </td>
                    <td
                      className={
                        (holding.gainAmount || 0) >= 0 ? styles.positive : styles.negative
                      }
                    >
                      {currency(holding.gainAmount)}
                      {holding.gainPercentage == null
                        ? ""
                        : ` (${holding.gainPercentage.toFixed(2)}%)`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>
            <UploadFileIcon />
          </div>
          No external holdings have been imported for this investor yet.
        </div>
      )}
    </>
  );
};

export default ExternalCasPanel;
